import { useCallback, useRef, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { transcodeOnClient as transcodeMediaClient } from '@joc/media-transcoder/client';
import { getFileUrl, resolveNormalizedFile, uploadAttachment } from 'core/media/uploadPreparedMedia';

export type FileUploadStatus = 'idle' | 'client-transcoding' | 'uploading' | 'server-processing' | 'success' | 'error';

export type FileCategory = 'image' | 'video' | 'document' | 'other';

export interface UseFileUploadState {
	status: FileUploadStatus;
	progress: number;
	fileName: string | null;
	fileUrl: string | null;
	file: File | null;
	error: string | null;
	fileCategory: FileCategory | null;
}

export interface UseFileUploadResult {
	upload: (file: File) => Promise<string | null>;
	reset: () => void;
	abort: () => void;
	status: FileUploadStatus;
	progress: number;
	fileName: string | null;
	fileUrl: string | null;
	file: File | null;
	error: string | null;
	isClientTranscoding: boolean;
	isUploading: boolean;
	isServerProcessing: boolean;
	isLoading: boolean;
	isSuccess: boolean;
	isError: boolean;
	fileCategory: FileCategory | null;
	processingTooltip: string;
}

export const getFileCategory = (file: File): FileCategory => {
	const mime = (file.type || '').toLowerCase();
	const name = file.name.toLowerCase();

	if (mime.startsWith('image/') || /\.(jpg|jpeg|png|webp|heic|heif|avif|tiff|gif|bmp|svg)$/i.test(name)) {
		return 'image';
	}

	if (mime.startsWith('video/') || /\.(mp4|mov|webm|avi|mkv|hevc|h265|m4v|mpeg|mpg|wmv|flv|ogv|3gp)$/i.test(name)) {
		return 'video';
	}

	if (
		mime === 'application/pdf' ||
		mime.includes('word') ||
		mime.includes('excel') ||
		mime.includes('spreadsheet') ||
		mime === 'text/plain' ||
		mime === 'text/csv' ||
		/\.(pdf|doc|docx|xls|xlsx|csv|txt)$/i.test(name)
	) {
		return 'document';
	}

	return 'other';
};

export const isWebpImage = (file: File): boolean => file.type === 'image/webp' || /\.webp$/i.test(file.name);

export const getProcessingTooltip = (status: FileUploadStatus, category: FileCategory | null): string => {
	switch (status) {
		case 'client-transcoding':
			return 'Оптимізація зображення на стороні клієнта у формат WebP...';
		case 'uploading':
			return 'Завантаження файлу на сервер...';
		case 'server-processing':
			if (category === 'video') {
				return 'Файл уже завантажено на сервер. Наразі виконується серверна конвертація та оптимізація відео';
			}
			if (category === 'document') {
				return 'Файл уже завантажено на сервер. Наразі виконується валідація та обробка документа';
			}
			return 'Файл уже завантажено на сервер. Наразі виконується оптимізація та конвертація медіа';
		case 'success':
			return 'Файл успішно завантажено та збережено';
		case 'error':
			return 'Помилка під час завантаження або обробки файлу';
		default:
			return 'Очікування файлу для завантаження';
	}
};

const initialState: UseFileUploadState = {
	status: 'idle',
	progress: 0,
	fileName: null,
	fileUrl: null,
	file: null,
	error: null,
	fileCategory: null,
};

export const useFileUpload = (): UseFileUploadResult => {
	const [state, setState] = useState<UseFileUploadState>(initialState);
	const cancelSourceRef = useRef<CancelTokenSource | null>(null);
	const abortedRef = useRef(false);

	const abort = useCallback(() => {
		abortedRef.current = true;
		if (cancelSourceRef.current) {
			cancelSourceRef.current.cancel('Upload aborted by user');
			cancelSourceRef.current = null;
		}
		setState(prev => ({
			...prev,
			status: 'idle',
			error: null,
		}));
	}, []);

	const reset = useCallback(() => {
		abortedRef.current = true;
		if (cancelSourceRef.current) {
			cancelSourceRef.current.cancel('Upload reset');
			cancelSourceRef.current = null;
		}
		setState(initialState);
	}, []);

	const upload = useCallback(async (file: File): Promise<string | null> => {
		abortedRef.current = false;
		if (cancelSourceRef.current) {
			cancelSourceRef.current.cancel('New upload initiated');
			cancelSourceRef.current = null;
		}

		const category = getFileCategory(file);
		const normalizedFile = resolveNormalizedFile(file);

		let fileToUpload: File = normalizedFile;

		// 1. Selective Client-Side Transcoding:
		// ONLY images should attempt client-side transcoding to WebP via @joc/media-transcoder/client.
		// If the image is already .webp, SKIP client transcoding.
		// For videos, documents, and non-image files, completely skip client transcoding.
		if (category === 'image' && !isWebpImage(normalizedFile)) {
			setState({
				status: 'client-transcoding',
				progress: 0,
				fileName: null,
				fileUrl: null,
				file: normalizedFile,
				fileCategory: category,
				error: null,
			});

			try {
				const transcodeResult = await transcodeMediaClient(normalizedFile);
				if (abortedRef.current) return null;
				if (transcodeResult?.file) {
					fileToUpload = resolveNormalizedFile(transcodeResult.file);
				}
			} catch (transcodeError) {
				if (abortedRef.current) return null;
				console.warn(
					'[useFileUpload] Client-side image transcoding to WebP failed, falling back to original file:',
					transcodeError
				);
				fileToUpload = normalizedFile;
			}
		}

		if (abortedRef.current) return null;

		// 2. Upload to server: POST /files/upload-attachment with only binary file
		setState({
			status: 'uploading',
			progress: 0,
			fileName: null,
			fileUrl: null,
			file: fileToUpload,
			fileCategory: category,
			error: null,
		});

		const source = axios.CancelToken.source();
		cancelSourceRef.current = source;

		try {
			const uploadedFileName = await uploadAttachment(fileToUpload, {
				cancelToken: source.token,
				onProgress: percent => {
					if (abortedRef.current) return;
					setState(prev => ({
						...prev,
						status: percent < 100 ? 'uploading' : 'server-processing',
						progress: percent,
					}));
				},
				onProcessing: () => {
					if (abortedRef.current) return;
					setState(prev => ({
						...prev,
						status: 'server-processing',
						progress: 100,
					}));
				},
			});

			if (abortedRef.current) return null;

			const publicUrl = getFileUrl(uploadedFileName);

			setState(prev => ({
				...prev,
				status: 'success',
				progress: 100,
				fileName: uploadedFileName,
				fileUrl: publicUrl,
				error: null,
			}));

			return uploadedFileName;
		} catch (err) {
			if (abortedRef.current || axios.isCancel(err)) {
				return null;
			}
			const errorMessage = err instanceof Error ? err.message : String(err);
			setState(prev => ({
				...prev,
				status: 'error',
				error: errorMessage,
			}));
			return null;
		} finally {
			if (cancelSourceRef.current === source) {
				cancelSourceRef.current = null;
			}
		}
	}, []);

	const isClientTranscoding = state.status === 'client-transcoding';
	const isUploading = state.status === 'uploading';
	const isServerProcessing = state.status === 'server-processing';
	const isLoading = isClientTranscoding || isUploading || isServerProcessing;
	const isSuccess = state.status === 'success';
	const isError = state.status === 'error';
	const processingTooltip = getProcessingTooltip(state.status, state.fileCategory);

	return {
		upload,
		reset,
		abort,
		status: state.status,
		progress: state.progress,
		fileName: state.fileName,
		fileUrl: state.fileUrl,
		file: state.file,
		error: state.error,
		isClientTranscoding,
		isUploading,
		isServerProcessing,
		isLoading,
		isSuccess,
		isError,
		fileCategory: state.fileCategory,
		processingTooltip,
	};
};
